/**
 * Hospital + ICU capacity projection.
 *
 * Given an infectious curve I(t) + per-pathogen IHR + LOS distribution
 * + ICU fraction, project bed occupancy + days-to-saturation.
 *
 * Method: hospital census at time t is the convolution of new
 * admissions (= incidence × IHR) with the survival function of the
 * LOS distribution. ICU census analogous with ICU LOS.
 *
 *   H(t) = Σ_{s≥0} A(t-s) × (1 - F_LOS(s))
 *
 * v0.2 uses gamma-distributed LOS; v0.4 will support empirical LOS
 * distributions from per-region hospital data.
 */

export interface HospitalParams {
  /** Daily new symptomatic infections (e.g., from SEIR I-compartment incidence). */
  dailyIncidence: number[];
  /** Infection → hospitalisation ratio (fraction of infections admitted). */
  ihr: number;
  /** Hospital length-of-stay mean (days). */
  losMeanDays: number;
  /** Hospital LOS standard deviation (days). */
  losSdDays: number;
  /** Fraction of hospital admissions also requiring ICU. */
  icuFraction: number;
  /** ICU LOS mean (days). */
  icuLosMeanDays: number;
  /** ICU LOS sd. */
  icuLosSdDays: number;
  /** Region bed capacity (total). For days-to-saturation. */
  hospitalBedsTotal?: number;
  /** Region ICU bed capacity. */
  icuBedsTotal?: number;
  /** Hospital surge multiplier (e.g., 1.5 = can flex 50% over baseline). */
  surgeMultiplier?: number;
}

export interface HospitalProjection {
  /** Daily admissions (≤ length of dailyIncidence). */
  admissions: number[];
  /** Daily hospital census (occupied bed-days). */
  hospitalCensus: number[];
  /** Daily ICU census. */
  icuCensus: number[];
  /** Day-index at which hospital census first exceeds capacity, or null. */
  hospitalSaturationDay: number | null;
  /** Day-index at which ICU census first exceeds capacity, or null. */
  icuSaturationDay: number | null;
}

/** Gamma-distributed LOS survival function P(LOS > s). */
function losSurvival(s: number, mean: number, sd: number): number {
  if (s < 0) return 1;
  if (mean <= 0 || sd <= 0) return s === 0 ? 1 : 0;
  // Gamma(α, β) with α = (mean/sd)², β = mean/sd².
  const beta = mean / (sd * sd);
  const alpha = mean * beta;
  // 1 - F(s; α, β) = upper regularised gamma Q(α, β s).
  return 1 - lowerRegGamma(alpha, beta * s);
}

function lowerRegGamma(a: number, x: number): number {
  if (x <= 0) return 0;
  if (x < a + 1) return seriesPart(a, x);
  return 1 - cfPart(a, x);
}

function lnGamma(x: number): number {
  const c = [
    76.18009172947146, -86.50532032941677, 24.01409824083091,
    -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5,
  ];
  let y = x, tmp = x + 5.5;
  tmp -= (x + 0.5) * Math.log(tmp);
  let ser = 1.000000000190015;
  for (let j = 0; j < 6; j++) { y += 1; ser += c[j]! / y; }
  return -tmp + Math.log(2.5066282746310005 * ser / x);
}

function seriesPart(a: number, x: number): number {
  const ln = lnGamma(a);
  let ap = a, sum = 1 / a, del = sum;
  for (let n = 1; n < 200; n++) {
    ap += 1; del *= x / ap; sum += del;
    if (Math.abs(del) < Math.abs(sum) * 1e-12) break;
  }
  return sum * Math.exp(-x + a * Math.log(x) - ln);
}

function cfPart(a: number, x: number): number {
  const ln = lnGamma(a);
  let b = x + 1 - a, c = 1 / 1e-30, d = 1 / b, h = d;
  for (let i = 1; i < 200; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = b + an / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < 1e-12) break;
  }
  return Math.exp(-x + a * Math.log(x) - ln) * h;
}

export function projectHospitalCensus(params: HospitalParams): HospitalProjection {
  const n = params.dailyIncidence.length;
  const admissions = params.dailyIncidence.map(i => i * params.ihr);
  const icuAdmissions = admissions.map(a => a * params.icuFraction);

  // Pre-compute survival table out to ~5× mean to keep convolution tractable.
  const hospHorizon = Math.ceil(params.losMeanDays * 5 + 20);
  const icuHorizon = Math.ceil(params.icuLosMeanDays * 5 + 20);
  const hospSurv: number[] = [];
  const icuSurv: number[] = [];
  for (let s = 0; s <= hospHorizon; s++) hospSurv.push(losSurvival(s, params.losMeanDays, params.losSdDays));
  for (let s = 0; s <= icuHorizon; s++) icuSurv.push(losSurvival(s, params.icuLosMeanDays, params.icuLosSdDays));

  const hospitalCensus = new Array<number>(n).fill(0);
  const icuCensus = new Array<number>(n).fill(0);
  for (let t = 0; t < n; t++) {
    for (let s = 0; s <= Math.min(hospHorizon, t); s++) {
      hospitalCensus[t]! += admissions[t - s]! * hospSurv[s]!;
    }
    for (let s = 0; s <= Math.min(icuHorizon, t); s++) {
      icuCensus[t]! += icuAdmissions[t - s]! * icuSurv[s]!;
    }
  }

  const surge = params.surgeMultiplier ?? 1;
  const hospCap = params.hospitalBedsTotal !== undefined ? params.hospitalBedsTotal * surge : Infinity;
  const icuCap = params.icuBedsTotal !== undefined ? params.icuBedsTotal * surge : Infinity;
  let hospSaturationDay: number | null = null;
  let icuSaturationDay: number | null = null;
  for (let t = 0; t < n; t++) {
    if (hospSaturationDay === null && hospitalCensus[t]! > hospCap) hospSaturationDay = t;
    if (icuSaturationDay === null && icuCensus[t]! > icuCap) icuSaturationDay = t;
  }

  return { admissions, hospitalCensus, icuCensus, hospitalSaturationDay: hospSaturationDay, icuSaturationDay };
}
